import * as React from "react";
import { useSwipeable } from "react-swipeable";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardMedia from "@mui/material/CardMedia";
import CardActions from "@mui/material/CardActions";
import Avatar from "@mui/material/Avatar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import axios from "../MyAxios";
import { toast } from "react-toastify";
import { DELETE_POST, POST, REPORT_FORM } from "../constants/api";
import PostButton from "./PostButton";
import { useTheme } from "@emotion/react";
import share from "../assets/img/share.svg";
import link from "../assets/img/link.svg";
import commentimg from "../assets/img/comment.svg";
import report from "../assets/img/report.svg";
import saved from "../assets/img/saved.svg";
import save from "../assets/img/save.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import clsx from "clsx";
import { calTime, parseHTML, regexReplacers } from "../helpers";
import deleteImg from "../assets/img/deleteDark.svg";
import { useDispatch, useSelector } from "react-redux";
import backImg from "@images/back.svg";
import VoteDownImg from "@images/voteDown.svg";
import VoteDownGreenImg from "@images/voteDownGreen.svg";
import { Box, CircularProgress } from "@mui/material";
import Loader from "./Loader";
import ExpandableText from "./ExpandableText";
import ImageModal from "./ImageModal";
import {
  setClickedPostId,
  setPostSubscribeStatus,
} from "@redux/prevPostsSlice";
import {
  ArrowForward,
  Bookmark,
  BookmarkBorder,
  Check,
  ChevronLeft,
  ChevronRight,
  Delete as DeleteIcon,
  NotificationsActive,
  NotificationsNoneOutlined,
  Person,
} from "@mui/icons-material";
import ShareDialog from "./share-post/ShareDialog";
import OpenGraphInfo from "./OpenGraphInfo";
import green_tick from "../assets/img/green_tick.png";
import cross from "../assets/img/cross.png";
import maybe from "../assets/img/maybe.png";
import calendar from "../assets/img/calendar.png";
import members from "../assets/img/members.png";
import publicIcon from "../assets/img/publicIcon.png";
import fees from "../assets/img/fees.png";
import MentionedTextWrapper from "./MentionedTextWrapper";
import SwipeablePostCard from "./SwipeablePostCard";
import BookmarkWithInnerIcon from "./BookmarkWithInnerIcon";
import CustomShareIcon from "./CustomShareIcon";
import CustomCommentIcon from "./CustomCommentIcon";
import VideoPlayerCard from "./VideoPlayerCard";


function ActionImageComponent({ action, anonymousClickWrapper }) {
  const IconComponent = action.icon;

  return action.icon && action.icon != null ?
    <IconComponent
      key={action.id}
      className={clsx(
        "cursor-pointer",
        action.className ?? "",
      )}
      style={{
        opacity: action.disabled ? 0.2 : 1,
        color: action.color,
      }}
      onClick={action.disabled ? null : (e) => {
        e.stopPropagation();
        anonymousClickWrapper(action.onClick, false);
      }}
    />
    : <img
      src={action.src}
      key={action.id}
      className={clsx(
        "cursor-pointer",
        action.className ?? "",
      )}
      style={{ opacity: action.disabled ? 0.2 : 1 }}
      onClick={action.disabled ? null : (e) => {
        e.stopPropagation();
        anonymousClickWrapper(action.onClick);
      }}
      alt="Action"
    />;
}

function ActionCard({ action, anonymousClickWrapper, optionalLabel = null }) {
  return <div
    key={action.id}
    className="cursor-pointer"
    onClick={action.disabled ? null : (e) => {
      e.stopPropagation();
      anonymousClickWrapper(action.onClick);
    }}>
    {action.shown &&
      (action.render ? (
        action.render({
          key: action.id,
          style: { opacity: action.disabled ? 0.2 : 1 },
          onClick: action.disabled ? null : (e) => {
            e.stopPropagation();
            anonymousClickWrapper(action.onClick);
          },
          alt: "Action",
        })
      ) : (
        <div className="flex items-center text-center">
          <ActionImageComponent action={action} anonymousClickWrapper={anonymousClickWrapper} />
          {optionalLabel && <Typography className="mt-1 text-xs md:text-base">
            {optionalLabel}
          </Typography>}
        </div>
      ))}
  </div>
}

function MediaPreview({ className, src, alt, isVideo, onClick = () => { } }) {
  return (
    <Card
      elevation={0}
      sx={{
        width: {
          xs: "35%",
          md: "50%",
        },
        maxWidth: 220,
        aspectRatio: "9/16",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        borderRadius: 2,
        overflow: "hidden",
      }}
      className={clsx(className)}
      onClick={onClick}
    >
      {isVideo ? <VideoPlayerCard
        className="w-full rounded-2xl overflow-hidden bg-black"
        src={src}
      /> : <CardMedia
        component="img"
        image={src}
        alt={alt}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />}
    </Card>
  );
}

function ExpandableHTML({ elements, defaultNumToShow = 1 }) {
  const [showDescription, setShowDescription] = React.useState(false);

  return (
    <div>
      <Typography
        variant="body2"
        className="break-words text-base"
        sx={{
          display: '-webkit-box',
          overflow: 'hidden',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 4,
        }}
      >
        {elements.slice(0, defaultNumToShow)}
        {showDescription && elements.slice(defaultNumToShow, elements.length)}
      </Typography>

      {defaultNumToShow < elements.length && !showDescription && (
        <Typography
          onClick={(e) => {
            e.stopPropagation();
            setShowDescription(true);
          }}
          variant="body2"
          className="shrink-0 cursor-pointer"
          color='primary'
        >
          See More
        </Typography>
      )}
      {defaultNumToShow < elements.length && showDescription && (
        <Typography
          onClick={(e) => {
            e.stopPropagation();
            setShowDescription(false);
          }}
          variant="body2"
          className="shrink-0 cursor-pointer"
          color='primary'
        >
          See Less
        </Typography>
      )}
    </div>
  );
}

function stringToHTML(text) {
  const urlPattern = /(?:https?:\/\/|www\.)[A-Za-z0-9._~:/?#[\]@!$&'()*+,;=%-]+|[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z]{2,})+(?:\/[^\s<>'"]*)?/gi

  text = text
    ?.replace(
      urlPattern,
      (url) => {
        const href = /^https?:\/\//i.test(url)
          ? url
          : `https://${url.startsWith("www.") ? "" : "www."}${url}`;
        let displayText = url;
        try {
          displayText = new URL(href).hostname.replace(/^www\./, "");
        } catch (e) { }
        return `<a href="${href}" target="_blank" rel="noreferrer noopener" class="underline cursor-pointer text-blue-500">${displayText}</a>`;
      },
    )
    ?.replace(regexReplacers.emoji.exp, regexReplacers.emoji.rep)
    ?.replace(regexReplacers.style.exp, regexReplacers.style.rep)
    ?.replace(regexReplacers.script.exp, regexReplacers.script.rep) ?? "";

  return text.replace(/\n/g, "<br>");
}

export default function Post({
  profilePost,
  group,
  post,
  setPost,
  comment,
  savedItems,
  setDeletedId,
  removePost,
  back,
  setGetSaved,
  getSaved,
  voteStatus,
  canRemoveCommunityPost = false,
  handleRemoveCommunityPost,
  canRemoveCommunityGroupPost = false,
  handleRemoveCommunityGroupPost,
  fullTitle = false,
  fullDescription = false,
  readOnly = false,
  canDeletePost = false,
  isCompact = false,
  returnTo: returnToProp,
}) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userRecord } = useSelector((state) => state.user);
  const isAnonymousUser = !Boolean(userRecord?.id);

  const [isSaved, setIsSaved] = React.useState(false);
  const [showSaveIcon, setShowSaveIcon] = React.useState(true);  // correctly handle bookmark/unbookmark in compact view (normal view's unbookmark action may not show correctly, so this was added)
  const [reload, setReload] = React.useState(0);
  const [ownPost, setOwnPost] = React.useState(false);
  const [upvoteCount, setUpvoteCount] = React.useState(0);
  const [downvoteCount, setDownvoteCount] = React.useState(0);
  const [isUpvoted, setIsUpvoted] = React.useState(false);
  const [isDownvoted, setIsDownvoted] = React.useState(false);
  const [upvoteLoading, setUpvoteLoading] = React.useState(false);
  const [downvoteLoading, setDownvoteLoading] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [navigating, setNavigating] = React.useState(false);
  const [votesLoading, setVotesLoading] = React.useState(false);
  const [openImageModal, setOpenImageModal] = React.useState(false);
  const [optimisticCancelled, setOptimisticCancelled] = React.useState(false);
  const [attendeesCount, setAttendeesCount] = React.useState(
    (post?.attendees && post.attendees.length) || 0,
  );
  const [commentCount, setCommentCount] = React.useState(post?.comments || 0);
  const [isAttending, setIsAttending] = React.useState(
    !!(
      post?.attendees &&
      post.attendees.some((a) => String(a.id) === String(userRecord?.id))
    ),
  );
  const rsvpInFlight = React.useRef(false);
  const [rsvpStatus, setRsvpStatus] = React.useState(
    !!(
      post?.attendees &&
      post?.attendees.some((a) => String(a.id) === String(userRecord?.id))
    )
      ? "yes"
      : null,
  );
  const [postShareDialog, setPostShareDialog] = React.useState(false);
  const [localSubscribed, setLocalSubscribed] = React.useState(
    !!post?.isSubscribed,
  );
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [viewTimerStarted, setViewTimerStarted] = React.useState(false);
  const latestFetchedRef = React.useRef(false);
  const cardRef = React.useRef(null); // ✅ Reference to the card element
  const observerRef = React.useRef(null); // ✅ Reference to the IntersectionObserver

  // carousel post related
  const isCarouselPost = Boolean(post.carousel && post.carousel.items);  // carousel items can't be empty, so no check for that
  const primaryCarouselItem = !isCarouselPost ? null : post.carousel.items[0];  // primary item is at index 0
  const primaryMediaUrl = isCarouselPost
    ? (primaryCarouselItem.media?.currBucketUrl
      || primaryCarouselItem.metadata?.ogImage?.[0]?.url)
    : (post?.media?.currBucketUrl
      || post?.postLinkMetadata?.ogImage?.[0]?.url)
  const primaryMediaType = isCarouselPost
    ? primaryCarouselItem.media?.fileType
    : post?.media?.fileType;

  const [carouselActiveIndex, setCarouselActiveIndex] = React.useState(0);
  const [carouselActiveItem, setCarouselActiveItem] = React.useState(post?.carousel?.items?.[0]);

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (!isCarouselPost || post.carousel.items.length <= 1) return;
      const newIndex = carouselActiveIndex + 1;
      scrollToPage(newIndex < post.carousel.items.length ? newIndex : 0);
    },
    onSwipedRight: () => {
      if (!isCarouselPost || post.carousel.items.length <= 1) return;
      const newIndex = carouselActiveIndex - 1;
      scrollToPage(newIndex >= 0 ? newIndex : post.carousel.items.length - 1);
    },
    trackMouse: true,
    delta: 50,  // min distance to register the swipe
  });

  React.useEffect(() => {
    setCarouselActiveItem(post?.carousel?.items?.[carouselActiveIndex]);
  }, [carouselActiveIndex])

  const scrollToPage = (pageIndex) => {
    setCarouselActiveIndex(pageIndex);
  };

  React.useEffect(() => {
    const count = (post?.attendees && post.attendees.length) || 0;
    const attending = !!(
      post?.attendees &&
      post.attendees.some((a) => String(a.id) === String(userRecord?.id))
    );
    setAttendeesCount(count);
    setIsAttending(attending);
    if (attending) setRsvpStatus("yes");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post?.attendees, userRecord?.id]);

  let avatarUrl;

  if (group?.icon === null) {
    // 🚫 icon explicitly null → don't fallback
    avatarUrl = "";
  } else if (group?.icon?.currBucketUrl) {
    // ✅ valid group icon
    avatarUrl = group.icon.currBucketUrl;
  } else {
    // ✅ fallback only if icon is undefined or missing
    avatarUrl = post?.user?.media?.currBucketUrl;
  }

  let postUserName = post?.user?.userName || post?.organizer?.userName;
  const name = group?.name || postUserName;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const isEventPost = !!(
    post?.startTime ||
    post?.endTime ||
    post?.eventType ||
    post?.attendeesFrom ||
    post?.admissionFeeType ||
    (typeof post?.id === "string" && String(post.id).startsWith("event-"))
  );

  const eventPostId =
    typeof post?.id === "string" && post.id.startsWith("event-")
      ? post.id
      : `event-${post?.id}`;
  const apiId = isEventPost ? eventPostId : post?.id;
  const isCancelled = post?.status === "cancelled" || optimisticCancelled;
  const isEventPast = isEventPost && post?.endTime && new Date(post.endTime) < new Date();

  const anonymousClickWrapper = (originalClickFn, blockInCompact = true) => {
    // in compact view, all interactions are also stopped
    // they instead expand/collapse the card
    if (blockInCompact && isCompact) {
      setIsExpanded(!isExpanded);
      return;
    }

    if (isAnonymousUser) {
      localStorage.setItem("loginRedirect", `/post/${apiId}`);
      localStorage.setItem("loginRedirectCondition", "NOT-ANON");
      toast.info("Please log-in or sign up to engage with posts.");
      navigate("/login-details");
    } else {
      originalClickFn();
    }
  };

  // ✅ IMPROVED: Use Intersection Observer to only mark-viewed when event is actually visible
  React.useEffect(() => {
    const isHomePage = location.pathname === "/" || location.pathname === "";
    if (!isHomePage) return;

    if (!isEventPost || !post?.id || viewTimerStarted || !cardRef.current) return;

    // Resolve communityId once at effect time
    const communityId =
      post.groups[0]?.community ||
      (post?.communities && post.communities[0]?.id) ||
      (post?.groups && post.groups[0]?.community?.id);

    console.log(
      "📍 Setting up Intersection Observer for event:",
      post.id,
      "communityId:",
      communityId,
    );

    if (!communityId) {
      console.warn("⚠️ No communityId found, skipping view tracking");
      return;
    }

    const eventId =
      typeof post.id === "string" && post.id.startsWith("event-")
        ? post.id.replace("event-", "")
        : post.id;

    let viewTimer = null;
    let hasMarkedViewed = false;

    // ✅ IntersectionObserver callback
    const handleIntersection = (entries) => {
      const [entry] = entries;

      if (entry.isIntersecting && !hasMarkedViewed) {
        // ✅ Element is now visible in viewport
        console.log("👁️ Event card is now visible:", eventId);

        // Start a 1 second timer before marking as viewed
        viewTimer = setTimeout(async () => {
          try {
            console.log("✅ Firing mark-viewed for visible event:", eventId);
            await axios.post("/engagement/mark-viewed", {
              contentId: eventId,
              contentType: "event",
              communityId: communityId,
            });
            hasMarkedViewed = true;
            setViewTimerStarted(true);
            // Once marked, stop observing
            if (observerRef.current) {
              observerRef.current.unobserve(entry.target);
            }
          } catch (err) {
            console.error("Failed to mark resurfaced view:", err);
          }
        }, 1000);
      } else if (!entry.isIntersecting && viewTimer) {
        // ✅ Element scrolled out of view before timer completed
        console.log("🚫 Event card scrolled out of view before marking:", eventId);
        clearTimeout(viewTimer);
        viewTimer = null;
      }
    };

    // ✅ Create observer with generous threshold
    const observer = new IntersectionObserver(handleIntersection, {
      threshold: 0.5, // Trigger when 50% of card is visible
      rootMargin: "0px 0px 0px 0px",
    });

    observerRef.current = observer;
    observer.observe(cardRef.current);

    // Cleanup
    return () => {
      if (viewTimer) clearTimeout(viewTimer);
      if (observer) observer.disconnect();
      observerRef.current = null;
    };
  }, [post?.id, isEventPost, viewTimerStarted, location.pathname]); // ✅ Updated deps

  const formatEventTime = (start, end) => {
    try {
      const s = new Date(start);
      const e = new Date(end);
      const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const formatter = new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: viewerTz,
      });

      const tzFormatter = new Intl.DateTimeFormat("en-US", {
        timeZone: viewerTz,
        timeZoneName: "short",
      });

      const tz = tzFormatter
        .formatToParts(s)
        .find((p) => p.type === "timeZoneName")?.value;

      return `${formatter.format(s)} - ${formatter.format(e)} ${tz ? `(${tz})` : ""}`;
    } catch (err) {
      return "";
    }
  };

  // ── Build the Google Maps URL for an event location ───────────────────────
  // Geocoded event  → opens the exact coordinates pin on Maps
  // Free-text event → falls back to a Maps text search (still navigates correctly)
  // Google Maps link → returns the link directly
  // Virtual / no location → returns null (no link rendered)
  const buildMapsUrl = (post) => {
    // ✅ Check if location is a direct Google Maps link
    if (post?.location) {
      const mapsLinkMatch = post.location.match(
        /https?:\/\/(www\.)?google\.com\/maps|maps\.app\.goo\.gl/i,
      );
      if (mapsLinkMatch) {
        return post.location;
      }
    }
    // ✅ Geocoded address with coordinates → pin link
    if (
      post?.locationCoordinates?.lat != null &&
      post?.locationCoordinates?.lng != null
    ) {
      return `https://www.google.com/maps?q=${post.locationCoordinates.lat},${post.locationCoordinates.lng}`;
    }
    // ✅ locationFormatted is only set by the Places autocomplete path,
    //    so it's a real geocoded address — safe to search
    if (post?.locationFormatted) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(post.locationFormatted)}`;
    }
    // ❌ Plain free-text `location` (e.g. "at my home") → not linkable
    return null;
  };

  // Extract a single URL-like token from free text and normalize to https://... if possible
  const extractUrlFromText = (text) => {
    if (!text) return null;
    let t = String(text).trim();
    try {
      t = decodeURIComponent(t);
    } catch (e) { }
    t = t.replace(/^<|>$/g, "");
    const httpRegex = /https?:\/\/[^\s<>'")]+/i;
    const wwwRegex = /www\.[^\s<>'")]+/i;
    const domainRegex =
      /(?:https?:\/\/)?(?:www\.)?[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+(?:\/[^\s<>'"]*)?/i;
    const match =
      t.match(httpRegex) || t.match(wwwRegex) || t.match(domainRegex);
    if (!match) return null;
    let link = match[0];
    link = link.replace(/[.,;:!?')\]]+$/g, "");
    if (!/^https?:\/\//i.test(link))
      link = "https://" + (link.startsWith("www.") ? "" : "www.") + link;
    try {
      return new URL(link).toString();
    } catch (e) {
      return null;
    }
  };

  const MEETING_LINK_RE = /meet\.google\.com|zoom(?:\.us|\.com)|zoommtg:\/\//i;

  // React.useEffect(() => {
  //   if (!apiId) return;
  //   setVotesLoading(true);
  //   axios
  //     .get(`/post/vote-status/${apiId}`)
  //     .then((res) => {
  //       setIsUpvoted(res.data?.isUpvoted || false);
  //       setIsDownvoted(res.data?.isDownvoted || false);
  //       setUpvoteCount(res.data?.upvoteCount || 0);
  //       setDownvoteCount(res.data?.downvoteCount || 0);
  //       if (res.data?.preference) {
  //         setRsvpStatus(res.data.preference);
  //       }
  //     })
  //     .catch((err) => console.error("Failed to fetch vote status:", err))
  //     .finally(() => setVotesLoading(false));
  // }, [apiId]);

  React.useEffect(() => {
    if (!voteStatus) return;

    setIsUpvoted(voteStatus.isUpvoted || false);
    setIsDownvoted(voteStatus.isDownvoted || false);
    setUpvoteCount(voteStatus.upvoteCount || 0);
    setDownvoteCount(voteStatus.downvoteCount || 0);

    if (voteStatus.preference) {
      setRsvpStatus(voteStatus.preference);
    }
  }, [voteStatus]);

  React.useEffect(() => {
    setLocalSubscribed(!!post?.isSubscribed);
  }, [post?.isSubscribed]);
  React.useEffect(() => {
    setCommentCount(post?.comments || 0);
  }, [post?.comments]);

  React.useEffect(() => {
    if (!post?.id) return undefined;
    // ensure we only fetch latest post once unless explicitly requested
    const normalize = (id) => String(id ?? "").replace(/^event-/, "");
    const fetchLatestPost = async () => {
      try {
        // guard anonymous user against legacy posts that allowed null/empty description
        const res = isAnonymousUser ? { description: "" } : await axios.get(`${POST}/${apiId}`);
        const updated = res?.data?.data ?? res?.data;
        if (updated) {
          const merged =
            isCarouselPost ? {
              ...updated,
              id: post?.id ?? updated.id,
              items: updated.carousel.items.map((updated, index) => {
                const orig = post.carousel.items[index];
                return {
                  metadata:
                    updated.metadata ?? orig?.metadata ?? null,
                  link: updated.link ?? orig?.link ?? null,
                  hideLinkPreview:
                    updated.hideLinkPreview !== undefined
                      ? updated.hideLinkPreview
                      : (orig?.hideLinkPreview ?? false),
                };
              })
            }
              : {
                ...updated,
                id: post?.id ?? updated.id,
                postLinkMetadata:
                  updated.postLinkMetadata ?? post?.postLinkMetadata ?? null,
                postLink: updated.postLink ?? post?.postLink ?? null,
                hideLinkPreview:
                  updated.hideLinkPreview !== undefined
                    ? updated.hideLinkPreview
                    : (post?.hideLinkPreview ?? false),
              };
          if (setPost) setPost(merged);
          if (typeof merged.comments === "number")
            setCommentCount(merged.comments);
        }
      } catch (err) { }
    };
    // STRICT GUARD: Only fetch if we are missing core data and we haven't fetched recently
    const hasData = !!(post?.title || post?.description || post?.content || post?.startTime);
    if (!hasData && !latestFetchedRef.current) {
      latestFetchedRef.current = true;
      fetchLatestPost();
    }

    const onPostCommented = (e) => {
      const detail = e?.detail || {};
      const pid = detail.postId ?? e?.postId;
      const count = detail.count;
      if (!pid) return;
      if (normalize(pid) === normalize(apiId)) {
        if (typeof count === "number") {
          setCommentCount(count);
          if (setPost) setPost({ ...post, comments: count });
        } else {
          fetchLatestPost();
        }
      }
    };
    const onStreamRefresh = () => {
      if (fullDescription) return; // ✅ skip re-fetch on ViewEvent/ViewPost full-detail pages
      fetchLatestPost();
    };
    const onStorage = (e) => {
      try {
        if (!e.key) return;
        if (/comment|post|stream/i.test(String(e.key))) {
          if (e.newValue) {
            try {
              const payload = JSON.parse(e.newValue);
              if (payload && (payload.postId || payload.id)) {
                onPostCommented({ detail: payload });
                return;
              }
            } catch (err) { }
          }
          onStreamRefresh();
        }
      } catch (err) { }
    };
    const commentEvents = [
      "post:commented",
      "comment:created",
      "comment:added",
      "comment:created:post",
      "comment:added:post",
      "post:comment:created",
    ];
    commentEvents.forEach((ev) => window.addEventListener(ev, onPostCommented));
    window.addEventListener("stream:refresh", onStreamRefresh);
    window.addEventListener("storage", onStorage);
    return () => {
      commentEvents.forEach((ev) =>
        window.removeEventListener(ev, onPostCommented),
      );
      window.removeEventListener("stream:refresh", onStreamRefresh);
      window.removeEventListener("storage", onStorage);
    };
  }, [post?.id, apiId, setPost, fullDescription]);

  const handleClickedPostSave = () => {
    const postId = group ? `group-${group.id}-${post.id}` : `post-${post.id}`;
    dispatch(setClickedPostId(postId));
    localStorage.postClickTrigger = postId;
  };

  const viewPost = (id) => {
    if (!id || navigating) return;
    try {
      handleClickedPostSave();
      setNavigating(true);
      console.log("is Event Post: ", isEventPost);

      if (isEventPost) {
        const eventId =
          typeof post?.id === "string" && String(post.id).startsWith("event-")
            ? String(post.id).replace("event-", "")
            : post?.id;
        console.log("|| event ID: ", eventId);

        navigate(`/event/${eventId}`);
      } else {
        console.log("is Event Post32: ", isEventPost);

        navigate(`${POST}/${id}`);
      }
    } catch (error) {
      toast.error(error);
    } finally {
      setNavigating(false);
    }
  };

  const setSaved = () => {
    if (savedItems?.length > 0) {
      let flag = false;
      savedItems.forEach((item) => {
        if (post?.id === item?.postSaved) flag = true;
      });
      setIsSaved(flag);
    } else {
      setIsSaved(post?.isSaved);
      setShowSaveIcon(!post?.isSaved);
    }
  };

  React.useEffect(() => {
    setSaved();
  }, [savedItems, post, group]);
  React.useEffect(() => {
    if (userRecord?.id && post?.user?.id && userRecord.id === post.user.id)
      setOwnPost(true);
  }, [userRecord, post]);

  let isGroupAdmin = false,
    isGroupModerator = false,
    isCommunityAdmin = false,
    isCommunityAdminMemberOfGroup = false;
  try {
    if (isAnonymousUser) { throw Error("Not an admin") }
    if (group) {
      if (group.admin && group.admin.id === userRecord?.id) isGroupAdmin = true;
      if (
        group.moderators &&
        Array.isArray(group.moderators) &&
        group.moderators.some((m) => m.id === userRecord?.id)
      )
        isGroupModerator = true;
    }
    const postGroupIds = (post?.groups || []).map((g) =>
      g && typeof g === "object" ? g.id : g,
    );
    const postCommunity =
      (group && group.community) ||
      (post?.communities && post.communities[0]) ||
      (post?.groups && post.groups[0] && post.groups[0].community);
    try {
      if (postCommunity) {
        const commAdmin = postCommunity.admin || postCommunity.adminId || null;
        const commAdminId =
          typeof commAdmin === "object" ? commAdmin.id : commAdmin;
        if (
          commAdminId &&
          String(commAdminId) === String(userRecord?.id) &&
          postCommunity?.adminAccessLevels?.canDeleteCommunityPost
        )
          isCommunityAdmin = true;
      }
    } catch (e) { }
    const userHasGroup = (gid) => {
      if (!userRecord) return false;
      for (const key of [
        "allGroups",
        "groups",
        "groupsAdmin",
        "groupsModerated",
      ]) {
        const arr = userRecord[key];
        if (
          Array.isArray(arr) &&
          arr.some((it) => String((it && it.id) || it) === String(gid))
        )
          return true;
      }
      return false;
    };
    isCommunityAdminMemberOfGroup = postGroupIds.some((gid) =>
      userHasGroup(gid),
    );
    if (userRecord?.groupsAdmin && Array.isArray(userRecord.groupsAdmin)) {
      if (userRecord.groupsAdmin.some((ug) => postGroupIds.includes(ug.id)))
        isGroupAdmin = true;
    }
    if (
      userRecord?.groupsModerated &&
      Array.isArray(userRecord.groupsModerated)
    ) {
      if (userRecord.groupsModerated.some((ug) => postGroupIds.includes(ug.id)))
        isGroupModerator = true;
    }
    if (post?.groups && Array.isArray(post.groups)) {
      for (const g of post.groups) {
        if (g && g.admin && g.admin.id === userRecord?.id) isGroupAdmin = true;
        if (
          g &&
          g.moderators &&
          Array.isArray(g.moderators) &&
          g.moderators.some((m) => m.id === userRecord?.id)
        )
          isGroupModerator = true;
      }
    }
  } catch (e) {
    isGroupAdmin = false;
    isGroupModerator = false;
  }

  const isGroupAdminOrModerator = isGroupAdmin || isGroupModerator;
  const canEditPost = !isAnonymousUser && (
    isEventPost
      ? ownPost || isGroupAdminOrModerator
      : ownPost);

  const onDelete = async (
    postId,
    confirmText = "Are you sure you want to delete this post?",
  ) => {
    if (window.confirm(confirmText)) {
      try {
        await axios.post(DELETE_POST, { postId: apiId }).then((res) => {
          toast.success(res.data?.message ?? "Post deleted successfully");
          if (removePost) removePost();
          setDeletedId?.(postId);
        });
      } catch (error) { }
    }
  };

  const RSVPButtons = ({ onYes, onNo, onMaybe }) => {
    const isDisabled = isCancelled || isEventPast;
    const btnStyle = (isSelected) => ({
      opacity: isDisabled ? 0.3 : 1,
      transform: isSelected ? "scale(1.2)" : "scale(1)",
      pointerEvents: isDisabled ? "none" : "auto",
      transition: "transform 0.15s ease",
    });

    const barStyle = (color, isSelected) => ({
      height: "3px",
      borderRadius: "2px",
      marginTop: "5px",
      backgroundColor: isSelected ? color : "transparent",
      transition: "background-color 0.2s ease",
    });

    return (
      <div className="flex items-center -ml-2">
        <div className="flex flex-col items-center mx-2">
          <img
            src={green_tick}
            alt="Yes"
            title="Yes"
            className="h-5 w-5"
            style={btnStyle(rsvpStatus === "yes")}
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onYes === "function") onYes(e);
            }}
          />
          <div style={barStyle("#22c55e", rsvpStatus === "yes")} className="w-full" />
        </div>
        <div className="flex flex-col items-center mx-2">
          <img
            src={maybe}
            alt="Maybe"
            title="Maybe"
            className="h-5 w-5"
            style={btnStyle(rsvpStatus === "maybe")}
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onMaybe === "function") onMaybe(e);
            }}
          />
          <div style={barStyle("#f59e0b", rsvpStatus === "maybe")} className="w-full" />
        </div>
        <div className="flex flex-col items-center mx-2">
          <img
            src={cross}
            alt="No"
            title="No"
            className="h-5 w-5"
            style={btnStyle(rsvpStatus === "no")}
            onClick={(e) => {
              e.stopPropagation();
              if (typeof onNo === "function") onNo(e);
            }}
          />
          <div style={barStyle("#ef4444", rsvpStatus === "no")} className="w-full" />
        </div>
      </div>
    );
  };

  const postWasOnlySharedWithConnections =
    post?.connections?.length > 1 &&
    !post.communities?.length &&
    !post.groups?.length;
  // Consider groups privacy: external share should be disabled only when ALL groups are private.
  const allGroupsPrivate = Boolean(
    post?.groups &&
    Array.isArray(post.groups) &&
    post.groups.length > 0 &&
    post.groups.every(
      (g) => String(g?.visibility || "").toLowerCase() === "private",
    ),
  );
  const shareExternallyDisabled =
    !isMobile || allGroupsPrivate || postWasOnlySharedWithConnections;
  const shareInternallyDisabled = postWasOnlySharedWithConnections;

  // unused new icon, reverted back to the old one for consistency across the app
  // const BookmarkWithTick = (props) => (
  //   <BookmarkWithInnerIcon
  //     InnerIcon={Check}
  //     bookmarkColor={theme.palette.primary.main} innerColor={theme.palette.primary.contrastText}
  //     bookmarkSize={2 * 11} innerSize={11}
  //     {...props}
  //   />
  // );

  const actions = [
    {
      id: 1,
      // src: share,
      icon: CustomShareIcon,
      className: "ml-[0.3rem] relative -bottom-[0.1rem] md:-bottom-[0.15rem]",
      disabled: readOnly,
      // hide share entirely when all groups are private
      shown:
        // Let P = "all groups are private", C = "only shared with connections", M = "is a mobile device"
        // below condition =  ~P & ( ~C | ~( ~M | P | C ) ) = ~P & ( ~C | M & ~P & ~C ) = ~P & ~C
        // so, this shows the share icon if:
        //  Groups list is empty OR at least 1 public group exists
        //  AND this was shared with a group or a community (not just connections)
        !allGroupsPrivate &&
        (!shareInternallyDisabled || !shareExternallyDisabled),
      onClick: () => {
        if (shareInternallyDisabled) {
          let content = `${process.env.REACT_APP_BASE_URL ?? "http://localhost:3000"}/post/${post?.id}`;
          if (navigator.share)
            navigator.share({ title: content, url: content });
        } else {
          setPostShareDialog(true);
        }
      },
    },
    {
      id: 2,
      src: link,
      disabled: readOnly,
      shown: true,
      onClick: () => {
        navigator.clipboard.writeText(
          `${process.env.REACT_APP_BASE_URL ?? "http://localhost:3000"}/post/${post?.id}`,
        );
        toast.success("Link copied to clipboard");
      },
    },
    {
      id: 3,
      // src: commentimg,
      icon: CustomCommentIcon,
      onClick: () => viewPost(post?.id),
      disabled: readOnly,
      shown: comment,
    },
    {
      id: 4,
      src: report,
      onClick: () =>
        navigate(REPORT_FORM, { state: { id: apiId, report: "post" } }),
      disabled: readOnly,
      shown: true,
    },
    {
      id: 5,
      src: deleteImg,
      onClick: () => onDelete(post?.id),
      disabled: readOnly,
      shown:
        ownPost ||
        isGroupAdmin ||
        (isGroupModerator && isEventPost) ||
        (isCommunityAdmin &&
          isEventPost &&
          (!allGroupsPrivate || isCommunityAdminMemberOfGroup)),
      className: "-mt-[2px]",
    },
    {
      id: 6,
      src: save,
      // icon: BookmarkBorder,
      // color: "gray",
      onClick: () => onSavePost(post?.id),
      disabled: readOnly,
      shown: !ownPost && showSaveIcon,
    },
    {
      id: 7,
      src: saved,
      // icon: Bookmark,
      // color: theme.palette.primary.main,
      onClick: () => onUnSavePost(post?.id),
      disabled: readOnly,
      shown: !ownPost && !showSaveIcon,
    },
  ];

  const onSavePost = async (id) => {
    try {
      await axios.post("/save/post", { id });
      toast.success("Post saved successfully");
      setShowSaveIcon(false);
      if (Array.isArray(savedItems)) setGetSaved(!getSaved);
      else setIsSaved(true);
      setReload(reload + 1);
    } catch (error) { }
  };
  const onUnSavePost = async (id) => {
    try {
      await axios.post("/unsave/post", { id });
      toast.success("Post unsaved successfully");
      setShowSaveIcon(true);
      if (Array.isArray(savedItems)) setGetSaved(!getSaved);
      else setIsSaved(false);
      setReload(reload + 1);
    } catch (error) { }
  };
  const onUpvotePost = () => {
    if (readOnly || isCancelled) return;
    setUpvoteLoading(true);
    axios
      .post("/post/upvote", { postId: apiId })
      .then(() => {
        setUpvoteCount((c) => c + 1);
        setDownvoteCount((c) => (isDownvoted ? c - 1 : c));
        setIsUpvoted(true);
        setIsDownvoted(false);
      })
      .catch(() => { })
      .finally(() => setUpvoteLoading(false));
  };
  const onRemoveUpvotePost = () => {
    if (readOnly || isCancelled) return;
    setUpvoteLoading(true);
    axios
      .post("/post/upvote", { postId: apiId, remove: true })
      .then(() => {
        setUpvoteCount((c) => c - 1);
        setIsUpvoted(false);
      })
      .catch(() => { })
      .finally(() => setUpvoteLoading(false));
  };
  const onDownVotePost = () => {
    if (readOnly || isCancelled) return;
    setDownvoteLoading(true);
    const dvApiId = (() => {
      const isEvent = !!(
        post?.isEvent ||
        post?.startTime ||
        post?.endTime ||
        post?.eventType ||
        post?.attendeesFrom ||
        post?.admissionFeeType ||
        (typeof post?.id === "string" && String(post.id).startsWith("event-"))
      );
      if (isEvent)
        return typeof post.id === "string" && post.id.startsWith("event-")
          ? post.id
          : `event-${post.id}`;
      return post.id;
    })();
    axios
      .post("/post/downvote", { postId: dvApiId })
      .then(() => {
        setDownvoteCount((c) => c + 1);
        setUpvoteCount((c) => (isUpvoted ? c - 1 : c));
        setIsDownvoted(true);
        setIsUpvoted(false);
      })
      .catch(() => { })
      .finally(() => setDownvoteLoading(false));
  };
  const onRemoveDownVotePost = () => {
    if (readOnly || isCancelled) return;
    setDownvoteLoading(true);
    axios
      .post("/post/downvote", { postId: apiId, remove: true })
      .then(() => {
        setDownvoteCount((c) => c - 1);
        setIsDownvoted(false);
      })
      .catch(() => { })
      .finally(() => setDownvoteLoading(false));
  };
  const onUnSubscribe = () => {
    dispatch(setPostSubscribeStatus({ postId: apiId, isSubscribed: false }));
    setLocalSubscribed(false);
    if (setPost) setPost({ ...post, isSubscribed: false });
    try {
      axios.post("/post/unsubscribe", { postId: apiId });
    } catch (e) {
      dispatch(setPostSubscribeStatus({ postId: apiId, isSubscribed: true }));
      setLocalSubscribed(true);
      if (setPost) setPost({ ...post, isSubscribed: true });
    }
  };
  const onSubscribe = () => {
    dispatch(setPostSubscribeStatus({ postId: apiId, isSubscribed: true }));
    setLocalSubscribed(true);
    if (setPost) setPost({ ...post, isSubscribed: true });
    try {
      axios.post("/post/subscribe", { postId: apiId });
    } catch (e) {
      dispatch(setPostSubscribeStatus({ postId: apiId, isSubscribed: false }));
      setLocalSubscribed(false);
      if (setPost) setPost({ ...post, isSubscribed: false });
    }
  };
  const handleRsvpYes = async (e) => {
    e.stopPropagation();
    if (readOnly || isCancelled || !isEventPost || rsvpInFlight.current) return;
    if (rsvpStatus === "yes") {
      toast.info("You have already marked your attendance for this event.");
      return;
    }
    rsvpInFlight.current = true;
    try {
      let eventId = post?.id;
      if (typeof eventId === "string" && eventId.startsWith("event-"))
        eventId = eventId.replace("event-", "");
      await axios.post(`/event/attend/${eventId}`);
      axios.post("/post/preference", { contentId: String(eventId), contentType: "event", preference: "yes" }).catch(() => { });
      toast.success("You are added to attendees");
      const attendee = {
        id: userRecord?.id,
        userName: userRecord?.userName,
        media: userRecord?.media,
      };
      if (setPost) {
        setPost({ ...post, attendees: [...(post.attendees || []), attendee] });
      } else {
        setAttendeesCount((c) => c + 1);
      }
      setIsAttending(true);
      setRsvpStatus("yes");
    } catch (err) {
      toast.error("Unable to RSVP");
    } finally {
      rsvpInFlight.current = false;
    }
  };
  const handleRsvpNo = async (e) => {
    e.stopPropagation();
    if (readOnly || isCancelled || !isEventPost) return;
    try {
      let eventId = post?.id;
      if (typeof eventId === "string" && eventId.startsWith("event-"))
        eventId = eventId.replace("event-", "");
      if (rsvpStatus === "no") {
        toast.info("You have already marked your attendance for this event.");
        return;
      }
      // If user is not currently attending, mark as 'no' locally without calling API
      // Show the same informational message as when re-selecting 'No'

      // Otherwise, call API to unattend
      await axios.post(`/event/unattend/${eventId}`);
      axios.post("/post/preference", { contentId: String(eventId), contentType: "event", preference: "no" }).catch(() => { });
      toast.success("You have been removed from attendees");
      const remaining = (post.attendees || []).filter(
        (a) => String(a.id) !== String(userRecord?.id),
      );
      if (setPost) {
        setPost({ ...post, attendees: remaining });
      }
      setAttendeesCount(remaining.length);
      setIsAttending(false);
      setRsvpStatus("no");
    } catch (err) {
      toast.error("Unable to update RSVP");
    }
  };
  const handleRsvpMaybe = async (e) => {
    e.stopPropagation();
    if (readOnly || isCancelled || !isEventPost) return;
    if (rsvpStatus === "maybe") {
      toast.info("You have already marked your attendance for this event.");
      return;
    }
    let eventId = post?.id;
    if (typeof eventId === "string" && eventId.startsWith("event-"))
      eventId = eventId.replace("event-", "");
    // If user was attending (yes), unattend them first
    if (isAttending) {
      try {
        await axios.post(`/event/unattend/${eventId}`);
        const remaining = (post.attendees || []).filter(
          (a) => String(a.id) !== String(userRecord?.id),
        );
        if (setPost) {
          setPost({ ...post, attendees: remaining });
        } else {
          setAttendeesCount((c) => c - 1);
        }
        setIsAttending(false);
      } catch (err) {
        toast.error("Unable to update RSVP");
        return;
      }
    }
    axios.post("/post/preference", { contentId: String(eventId), contentType: "event", preference: "maybe" }).catch(() => { });
    setRsvpStatus("maybe");
    toast.success("Marked as maybe");
  };

  let postTitleText = post?.title;
  let postDescsTest = isCarouselPost
    ? post.carousel.items.map((item) => item.description)
    : [post?.description || " "];
  const maxPostDescriptionLength = 150;
  if (!fullTitle && postTitleText?.length >= 122)
    postTitleText = `${postTitleText?.slice(0, 122)}...`;
  if (!fullDescription) {
    postDescsTest = postDescsTest.map((desc) => {
      if (desc?.length >= maxPostDescriptionLength)
        desc = `${desc?.slice(0, maxPostDescriptionLength)}...`;
      return desc;
    });
  }

  const postTitle =
    postTitleText
      ?.replace(regexReplacers.emoji.exp, regexReplacers.emoji.rep)
      ?.replace(regexReplacers.style.exp, regexReplacers.style.rep)
      ?.replace(regexReplacers.script.exp, regexReplacers.script.rep) ?? "";

  const urlPattern = /(?:https?:\/\/|www\.)[A-Za-z0-9._~:/?#[\]@!$&'()*+,;=%-]+|[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z]{2,})+(?:\/[^\s<>'"]*)?/gi
  const postDescriptions =
    postDescsTest.map((postDescTest) =>
      postDescTest
        ?.replace(
          urlPattern,
          (url) => {
            const href = /^https?:\/\//i.test(url)
              ? url
              : `https://${url.startsWith("www.") ? "" : "www."}${url}`;
            let displayText = url;
            try {
              displayText = new URL(href).hostname.replace(/^www\./, "");
            } catch (e) { }
            return `<a href="${href}" target="_blank" rel="noreferrer noopener" class="underline cursor-pointer text-blue-500">${displayText}</a>`;
          },
        )
        ?.replace(regexReplacers.emoji.exp, regexReplacers.emoji.rep)
        ?.replace(regexReplacers.style.exp, regexReplacers.style.rep)
        ?.replace(regexReplacers.script.exp, regexReplacers.script.rep) ?? "");

  let parsedDescriptions = [""];
  if (postDescriptions)
    parsedDescriptions = postDescriptions.map(desc => desc.replace(/\n/g, "<br>"));

  const sentencesPerDescription = postDescsTest.map((postDescTest) => {
    const sentenceEndRegex = /([.!?]+|…+)(?=\s|$)/g;
    const rawParts = postDescTest.split(sentenceEndRegex);
    const sentences = [];
    for (let i = 0; i < rawParts.length; i += 2) {
      const text = rawParts[i] || '';
      const punct = rawParts[i + 1] || '';
      if (text.trim()) {
        sentences.push(text + punct);
      }
    }
    return sentences;
  });

  // display first sentence as Post Title in compact view for regular posts
  // event posts have their own title
  const index = !isCompact && isCarouselPost ? carouselActiveIndex : 0;
  const [firstSentence, setFirstSentence] = React.useState((postTitleText ? postTitleText : sentencesPerDescription?.[index] ? sentencesPerDescription[index][0] : postDescsTest[index]) ?? " ");
  const [firstThreeSentences, setFirstThreeSentences] = React.useState(sentencesPerDescription[index].slice(0, 3).join(' '));

  React.useEffect(() => {
    const index = !isCompact && isCarouselPost ? carouselActiveIndex : 0;
    setFirstSentence((postTitleText ? postTitleText : sentencesPerDescription?.[index] ? sentencesPerDescription[index][0] : postDescsTest[index]) ?? " ");
    setFirstThreeSentences(sentencesPerDescription[index].slice(0, 3).join(' '));
  }, [carouselActiveIndex]);

  if (loading || !post) return <Loader />;
  return isCompact ? (
    <div>
      {navigating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60">
          <CircularProgress />
        </div>
      )}
      <SwipeablePostCard
        elevation={0}
        sx={{
          boxShadow: "0 3px 10px 2px rgba(0, 0, 0, 0.30)",
        }}
        className={clsx(
          "z-0 mx-0 mb-5 mt-3 w-full overflow-hidden"
        )}
        onBookmark={() => showSaveIcon ? anonymousClickWrapper(actions[5].onClick, false) : anonymousClickWrapper(actions[6].onClick, false)}
        isBookmarked={!showSaveIcon}
        onCardClick={() => setIsExpanded(!isExpanded)}
        cardRef={cardRef}
      >
        <div className="pr-3 pl-3 sm:pl-6 py-2">
          <div className="flex my-1 md:my-4 mx-0 h-40 w-full min-w-0 max-h-32 md:max-h-none">

            {primaryMediaUrl && <MediaPreview
              className="mr-4 sm:mr-5 md:mr-6"
              isVideo={primaryMediaType === "video"}
              alt="Post Image"
              src={primaryMediaUrl}
              onClick={primaryMediaType && primaryMediaType !== "video"
                ? (e) => {
                  e.stopPropagation();
                  anonymousClickWrapper(() => setOpenImageModal(true));
                }
                : undefined}
            />}
            <div className="flex flex-col py-1 md:py-1 justify-between flex-1 min-w-0">
              <div className="cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="ml-1 mr-1 min-w-0">
                  <Typography className="font-semibold text-sm md:text-base text-wrap w-4/5 max-w-96"
                    sx={{
                      display: '-webkit-box',
                      overflow: 'hidden',
                      WebkitBoxOrient: 'vertical',
                      WebkitLineClamp: 3,
                    }}>
                    {firstSentence}
                  </Typography>

                  {/* ── NORMAL POST ── */}
                  {!isEventPost && (
                    <>
                      <div className="flex gap-1 mt-1 md:mt-3 flex-wrap min-w-0">
                        {post?.tags?.map((tag, key) => (
                          <Typography
                            key={key}
                            onClick={(e) => {
                              e.stopPropagation();
                              anonymousClickWrapper(() => navigate(`/search?search=%23${tag.subcategory}`));
                            }}
                            variant="body2"
                            color="primary"
                            className="font-semibold"
                          >
                            {`{${tag.subcategory}}`}
                            {key < post.tags.length - 1 ? "," : ""}
                          </Typography>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-start min-w-0 w-full">
                <CardActions
                  onClick={(e) => e.stopPropagation()}
                  disableSpacing
                  // className="flex justify-between my-0 py-1 px-1 w-full max-w-[17.5rem] md:max-w-[17.5rem]"
                  className="grid grid-cols-3 justify-items-stretch my-0 py-0 px-1 w-full min-w-0 max-w-[13rem] md:max-w-[17.5rem]"
                >
                  <div className="min-w-0 justify-self-start scale-[85%] md:scale-[100%]">
                    <ActionCard action={actions[2]} anonymousClickWrapper={anonymousClickWrapper}
                      optionalLabel={<Typography variant="primary" style={{ color: theme.palette.text.dark }}
                        className="text-base font-semibold opacity-75 ml-1">{`${commentCount}`}</Typography>} />
                  </div>
                  <div className="justify-self-center flex items-center -translate-x-5 md:-translate-x-4">
                    {votesLoading && <CircularProgress size="1rem" />}
                    <div
                      className={clsx(
                        "min-w-0",
                        // "flex py-1 rounded-full items-center gap-[2rem] md:gap-[1rem]",
                        "grid grid-cols-2 py-0 rounded-full items-center gap-[0.5rem] sm:gap-[0.9rem] md:gap-[2rem]",
                        votesLoading && "hidden",
                      )}
                    >
                      <div className="grid grid-cols-2 gap-4 items-center scale-[85%] md:scale-[100%]">
                        {upvoteLoading && <CircularProgress size="1rem" />}
                        {!upvoteLoading &&
                          (isUpvoted ? (
                            <img
                              alt="up"
                              style={{
                                height: "20px",
                                opacity: readOnly || isCancelled || isEventPast ? 0.3 : 1,
                                pointerEvents: readOnly || isCancelled || isEventPast ? "none" : "auto",
                              }}
                              className="rotate-180 max-w-none"
                              src={VoteDownGreenImg}
                              onClick={() => onRemoveUpvotePost()}
                            />
                          ) : (
                            <img
                              alt="up"
                              style={{
                                height: "20px",
                                opacity: readOnly || isCancelled || isEventPast ? 0.3 : 1,
                                pointerEvents: readOnly || isCancelled || isEventPast ? "none" : "auto",
                              }}
                              className="rotate-180 max-w-none"
                              src={VoteDownImg}
                              onClick={() => anonymousClickWrapper(() => onUpvotePost(), false)}
                            />
                          ))}
                        <Typography className="mt-1 text-base">
                          {upvoteCount}
                        </Typography>
                      </div>
                      <div className="grid grid-cols-2 gap-4 items-center scale-[85%] md:scale-[100%]">
                        {downvoteLoading && <CircularProgress size="1rem" />}
                        {!downvoteLoading &&
                          (isDownvoted ? (
                            <img
                              alt="down"
                              style={{
                                height: "20px",
                                opacity: readOnly || isCancelled || isEventPast ? 0.3 : 1,
                                pointerEvents: readOnly || isCancelled || isEventPast ? "none" : "auto",
                              }}
                              className="relative -top-[0.1rem] md:-top-[0.09rem] max-w-none"
                              src={VoteDownGreenImg}
                              onClick={() => onRemoveDownVotePost()}
                            />
                          ) : (
                            <img
                              alt="down"
                              style={{
                                height: "20px",
                                opacity: readOnly || isCancelled || isEventPast ? 0.3 : 1,
                                pointerEvents: readOnly || isCancelled || isEventPast ? "none" : "auto",
                              }}
                              className="relative -top-[0.1rem] md:-top-[0.09rem] max-w-none"
                              src={VoteDownImg}
                              onClick={() => anonymousClickWrapper(() => onDownVotePost(), false)}
                            />
                          ))}
                        <Typography className="mt-1 text-base">
                          {downvoteCount}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <div className="min-w-0 justify-self-end scale-[85%] md:scale-[100%]">
                    <ActionCard action={actions[0]} anonymousClickWrapper={anonymousClickWrapper}
                      optionalLabel={<Typography variant="primary" style={{ color: theme.palette.text.dark }}
                        className="text-sm underline underline-offset-[3px] font-semibold opacity-75 ml-2">Share</Typography>} />
                  </div>
                </CardActions>

                <CardHeader
                  className="my-0 py-1 px-1 w-full"
                  sx={{
                    "& .MuiCardHeader-avatar": {
                      marginRight: 1,
                    }
                  }}
                  avatar={
                    <div className="flex gap-3">
                      <div
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          anonymousClickWrapper(() => {
                            if (Boolean(group)) navigate(`/group/info/${group?.id}`);
                            else navigate(`/${postUserName}`);
                          });
                        }}
                      >
                        <Person sx={{ width: 28, height: 28 }} />
                      </div>
                    </div>
                  }
                  title={
                    <Typography sx={{ height: 28 }} className="text-sm flex items-end pb-1" onClick={isAnonymousUser ? () => anonymousClickWrapper(() => { }) : undefined}>
                      <Link
                        className="inline-flex"
                        to={
                          isAnonymousUser || isCompact ? undefined :  // block interactions in compact too / only expand/collapse the card
                            `/${postUserName}`
                        }
                      >
                        <Typography
                          color={theme.palette.text.disabled}
                          className="text-xs font-semibold"
                        >
                          Author: {postUserName}
                        </Typography>
                      </Link>
                    </Typography>
                  }
                />
              </div>
            </div>
            {!isMobile && <div className="ml-auto"><ActionCard action={showSaveIcon ? actions[5] : actions[6]} anonymousClickWrapper={(func) => anonymousClickWrapper(func, false)} /></div>}
          </div>

          {isExpanded && postDescriptions && postDescriptions.length > 0
            && <div className="mx-0 pr-0 pl-1 mb-7 md:mb-12">
              {(fullDescription ? (
                <Typography
                  variant="body2"
                  className="break-words text-base"
                >
                  {parseHTML(parsedDescriptions[0])}
                </Typography>
              ) : (
                <Typography
                  variant="body2"
                  className="break-words text-xs"
                  sx={{
                    display: '-webkit-box',
                    overflow: 'hidden',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: 3,
                  }}
                >
                  {parseHTML(stringToHTML(firstThreeSentences)).map((html, index) => {
                    if (typeof html === "string") {
                      return <Typography
                        key={index}
                        variant="body2"
                        className={"break-words text-xs text-wrap"}
                      >
                        <MentionedTextWrapper>
                          {html}
                        </MentionedTextWrapper>
                      </Typography>;
                    }
                    return html;
                  })}
                </Typography>
              ))}
              <Typography
                onClick={(e) => {
                  e.stopPropagation();
                  anonymousClickWrapper(() => viewPost(post?.id), false);
                }}
                variant="body2"
                className="shrink-0 cursor-pointer underline mt-3"
                color='primary'
              >
                Read More <ArrowForward />
              </Typography>
            </div>}
        </div>
      </SwipeablePostCard>


      {primaryMediaType !== "video" && (
        <ImageModal
          url={primaryMediaUrl}
          open={openImageModal}
          setOpen={setOpenImageModal}
        />
      )}
      <ShareDialog
        post={post}
        open={postShareDialog}
        onClose={() => setPostShareDialog(false)}
      />
    </div>
  )
    : (
      <div>
        {navigating && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60">
            <CircularProgress />
          </div>
        )}
        <Card ref={cardRef} className={clsx("z-0 mx-auto mb-5 shadow-none")}>
          <CardHeader
            avatar={
              <div className="flex gap-3">
                {back && (
                  <img
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      try {
                        const gid =
                          group?.id || (post?.groups && post.groups[0]?.id);
                        if (gid) {
                          try {
                            localStorage.removeItem(`group-${gid}-cache`);
                          } catch (e) { }
                        }
                        localStorage.setItem(
                          "stream-refresh",
                          Date.now().toString(),
                        );
                        window.dispatchEvent(new Event("stream:refresh"));
                      } catch (err) { }
                      navigate(-1);
                    }}
                    src={backImg}
                    alt="back"
                    className="self-center cursor-pointer"
                  />
                )}
                <div
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    anonymousClickWrapper(() => {
                      if (Boolean(group)) navigate(`/group/info/${group?.id}`);
                      else navigate(`/${postUserName}`);
                    });
                  }}
                >
                  <Avatar src={avatarUrl} />
                </div>
              </div>
            }
            action={
              isCancelled && !isGroupAdminOrModerator ? (
                <div
                  style={{
                    backgroundColor: "#22c55e",
                    color: "white",
                    borderRadius: 9999,
                    padding: "6px 16px",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  Event Cancelled
                </div>
              ) : canEditPost ? (
                <div style={{ display: "flex" }}>
                  <IconButton
                    disabled={readOnly}
                    sx={{ p: 0.5, borderRadius: 99999 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isEventPost) {
                        const eventId =
                          typeof post?.id === "string" &&
                            String(post.id).startsWith("event-")
                            ? String(post.id).replace("event-", "")
                            : post?.id;
                        const groupId =
                          group?.id || (post?.groups && post.groups[0]?.id);
                        navigate(`/group/${groupId}/events/create`, {
                          replace: true, // ✅ replaces ViewEvent so back stack stays clean
                          state: {
                            eventId,
                            eventData: post,
                            returnToGroupId: groupId,
                            // ✅ return to ViewEvent if we're currently on it
                            returnTo: returnToProp || location.pathname,
                          },
                        });
                      } else {
                        const returnToGroupId =
                          group?.id ||
                          (post?.groups && post.groups[0]?.id) ||
                          null;
                        // Navigate to the create route with the post id so CreatePost
                        // opens in edit mode. Also pass returnTo so CreatePost can
                        // navigate back to the originating page after saving.
                        navigate(`/${isCarouselPost ? "carousel-" : ""}post/create/${post?.id}`, {
                          replace: true,
                          state: {
                            returnToGroupId,
                            returnTo: returnToProp || location.pathname,
                          },
                        });
                      }
                    }}
                  >
                    <PostButton>Edit</PostButton>
                  </IconButton>
                  {isEventPost && (
                    <IconButton
                      disabled={readOnly || isCancelled}
                      sx={{ p: 0.5, borderRadius: 99999 }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (isCancelled) return;
                        if (
                          !window.confirm(
                            "Are you sure you want to cancel this event?",
                          )
                        )
                          return;
                        const prevStatus = post?.status;
                        setOptimisticCancelled(true);
                        if (setPost) setPost({ ...post, status: "cancelled" });
                        try {
                          const eventId =
                            typeof post?.id === "string" &&
                              String(post.id).startsWith("event-")
                              ? String(post.id).replace("event-", "")
                              : post?.id;
                          await axios.post(`/event/cancel/${eventId}`);
                          toast.success("Event cancelled");
                          try {
                            localStorage.setItem(
                              "stream-refresh",
                              Date.now().toString(),
                            );
                            window.dispatchEvent(new Event("stream:refresh"));
                          } catch (e) { }
                        } catch (err) {
                          setOptimisticCancelled(false);
                          if (setPost) setPost({ ...post, status: prevStatus });
                          toast.error("Unable to cancel event");
                        }
                      }}
                    >
                      <PostButton>
                        {isCancelled ? "Cancelled" : "Cancel"}
                      </PostButton>
                    </IconButton>
                  )}
                </div>
              ) : localSubscribed ? (
                <IconButton disabled={readOnly} onClick={() => anonymousClickWrapper(onUnSubscribe)}>
                  <NotificationsActive />
                </IconButton>
              ) : (
                <IconButton disabled={readOnly} onClick={() => anonymousClickWrapper(onSubscribe)}>
                  <NotificationsNoneOutlined />
                </IconButton>
              )
            }
            title={
              <Typography className="text-sm" onClick={isAnonymousUser ? () => anonymousClickWrapper(() => { }) : undefined}>
                <Link
                  className="inline-flex"
                  to={
                    isAnonymousUser ? undefined :
                      group && group.name
                        ? `/group/info/${group.id}`
                        : `/${post?.user?.userName}`
                  }
                >
                  <Typography
                    className={
                      profilePost
                        ? "text-sm font-semibold"
                        : "text-base font-semibold"
                    }
                  >
                    {name}
                  </Typography>
                </Link>
                <small
                  style={{ color: theme.palette.text.dark }}
                  className="ml-2"
                >
                  {calTime(post?.createdAt)}
                </small>
              </Typography>
            }
            subheader={
              !profilePost && (
                <div className="flex items-center gap-2" onClick={isAnonymousUser ? () => anonymousClickWrapper(() => { }) : undefined}>
                  {(Boolean(group) || profilePost) && (
                    <Link to={isAnonymousUser ? undefined : `/${post?.user?.userName}`}>
                      <Typography
                        className={
                          profilePost ? "text-sm font-semibold" : "text-xs"
                        }
                      >
                        {postUserName}
                      </Typography>
                    </Link>
                  )}
                  {profilePost && (
                    <small
                      className="mt-1"
                      style={{ color: theme.palette.text.dark }}
                    >
                      {new Date(post?.createdAt).toLocaleDateString()}
                    </small>
                  )}
                </div>
              )
            }
          />

          <div className="cursor-pointer" onClick={() => anonymousClickWrapper(() => viewPost(post?.id))}>
            <div className="mx-4 mb-2">
              {/* ── OG preview for ALL posts ── */}
              {!isEventPost && !isCarouselPost &&
                (post?.postLinkMetadata || post?.postLink) &&
                (post?.hideLinkPreview || !post?.postLinkMetadata ? (
                  <Link
                    target="_blank"
                    rel="noreferrer"
                    to={post?.postLink}
                    className="text-blue-500 hover:underline break-all"
                  >
                    {post?.postLink}
                  </Link>
                ) : (
                  <OpenGraphInfo
                    className="mb-4"
                    data={post?.postLinkMetadata}
                    imageOnly={isEventPost}
                  />
                ))}
              {(!isEventPost && isCarouselPost &&
                (carouselActiveItem?.metadata || carouselActiveItem?.link) &&
                <div {...handlers} className="relative w-full group touch-pan-y xs:mx-auto md:mx-12 md:max-w-[calc(100%-6rem)]">
                  <div className="overflow-hidden w-full">
                    <div
                      className="flex transition-transform duration-300 ease-in-out"
                      style={{ transform: `translateX(-${carouselActiveIndex * 100}%)` }}
                    >
                      {post.carousel.items.map((item, index) => (
                        <div key={index} className="w-full flex-shrink-0">
                          {(item.hideLinkPreview || !item.metadata) ? (
                            <Link
                              target="_blank"
                              rel="noreferrer"
                              to={item.link}
                              className="text-blue-500 hover:underline break-all"
                            >
                              {item.link}
                            </Link>
                          ) : (
                            <OpenGraphInfo
                              className="mb-4"
                              data={item.metadata}
                              imageOnly={isEventPost}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  {isCarouselPost && !isMobile && post.carousel.items.length > 1 && (
                    <div className="z-50">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newIndex = carouselActiveIndex - 1;
                          scrollToPage(newIndex >= 0 ? newIndex : post.carousel.items.length - 1);
                        }}
                        className="absolute -left-14 top-1/2 -translate-y-1/2 bg-transparent text-gray-700 p-2 z-10 flex items-center justify-center cursor-pointer"
                        aria-label="Previous slide"
                      >
                        {!isMobile && <span className="font-bold"><ChevronLeft sx={{ fontSize: "5rem" }} /></span>}
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const newIndex = carouselActiveIndex + 1;
                          scrollToPage(newIndex < post.carousel.items.length ? newIndex : 0);
                        }}
                        className="absolute -right-14 top-1/2 -translate-y-1/2 bg-transparent text-gray-700 p-2 z-10 flex items-center justify-center cursor-pointer"
                        aria-label="Next slide"
                      >
                        {!isMobile && <span className="font-bold"><ChevronRight sx={{ fontSize: "5rem" }} /></span>}
                      </button>
                    </div>
                  )}
                </div>)}

              {/* ── EVENT POST ── */}
              {isEventPost && (
                <div>
                  <div className="mb-4 rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                    {/* Event Header */}
                    <div className="p-4 pb-2">
                      <div className="flex items-start justify-between">
                        {/* Title should be allowed to shrink (min-w-0) so it doesn't push the attendee count
                          Attendee block is non-shrinking so it keeps a consistent size */}
                        <div className="font-semibold text-lg min-w-0 pr-4 break-words">
                          {post?.title}
                        </div>
                        {isGroupAdminOrModerator && (
                          <div className="flex items-center gap-1 text-gray-600 flex-shrink-0 ml-2 pr-2">
                            <img
                              src={members}
                              alt="members"
                              className="w-4 h-4"
                            />
                            <span className="text-sm font-medium">
                              {attendeesCount}
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600 mt-2 mb-2">
                        <img src={calendar} alt="calendar" className="w-4 h-4" />
                        <span>
                          {formatEventTime(
                            post?.startTime,
                            post?.endTime,
                          )}
                        </span>
                      </div>

                      {/* ── Location row ──────────────────────────────────────
                        Google Maps link → clickable link to the map
                        Geocoded  → clickable link opening Google Maps pin
                        Free-text → clickable link opening Google Maps search
                        Meeting link → clickable link to the meeting
                        Virtual / empty → plain "Location TBD" text          */}
                      <div className="flex items-start text-sm text-gray-600 gap-2 mb-3">
                        <span className="mt-0.5">📍</span>
                        {post?.location || post?.locationFormatted ? (
                          (() => {
                            const mapsUrl = buildMapsUrl(post);
                            const displayText =
                              post.locationFormatted || post.location;
                            // Check if location is a direct Google Maps link
                            const isDirectMapsLink =
                              post?.location &&
                              /https?:\/\/(www\.)?google\.com\/maps|maps\.app\.goo\.gl/i.test(
                                post.location,
                              );
                            // If this looks like a meeting link, render an open link instead of maps
                            const meetingLink = extractUrlFromText(
                              post.location || "",
                            );
                            if (
                              meetingLink &&
                              MEETING_LINK_RE.test(meetingLink)
                            ) {
                              return (
                                <a
                                  href={meetingLink}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  className="underline text-blue-500 hover:text-blue-700"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {displayText}
                                </a>
                              );
                            }
                            // If it's a direct Google Maps link, render it as clickable
                            if (isDirectMapsLink && mapsUrl) {
                              return (
                                <a
                                  href={mapsUrl}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  className="underline text-blue-500 hover:text-blue-700"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {displayText}
                                </a>
                              );
                            }
                            return mapsUrl ? (
                              <a
                                href={mapsUrl}
                                target="_blank"
                                rel="noreferrer noopener"
                                className="underline text-blue-500 hover:text-blue-700"
                                onClick={(e) => e.stopPropagation()}
                              >
                                {displayText}
                              </a>
                            ) : (
                              // ✅ Free-text address: shown as plain unclickable text
                              <span>{displayText}</span>
                            );
                          })()
                        ) : (
                          <span>Location TBD</span>
                        )}
                      </div>
                    </div>

                    {/* Media */}
                    {post?.media?.currBucketUrl &&
                      (post?.media?.fileType === "video" ? (
                        <VideoPlayerCard
                          className="h-52 w-full rounded-2xl overflow-hidden bg-black"
                          src={post?.media?.currBucketUrl}
                        />
                      ) : (
                        <CardMedia
                          onClick={(e) => {
                            e.stopPropagation();
                            anonymousClickWrapper(() => setOpenImageModal(true));
                          }}
                          component="img"
                          className="mx-auto h-52 w-11/12 rounded-2xl object-contain"
                          image={post?.media?.currBucketUrl}
                          alt="Stream Media"
                        />
                      ))}

                    {/* ── OG preview INSIDE event card, below media ── */}
                    {!post?.hideLinkPreview && post?.postLinkMetadata && (
                      <div>
                        <OpenGraphInfo
                          data={post?.postLinkMetadata}
                          imageOnly={isEventPost}
                        />
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="px-2 pb-2">

                    {postDescriptions && postDescriptions.length > 0 && (
                      <>
                        <div className=" font-semibold text-lg">
                          {post?.title}
                        </div>

                        {fullDescription ? (
                          <Typography
                            variant="body2"
                            className="break-words text-base"
                          >
                            {parseHTML(parsedDescriptions[0])}
                          </Typography>
                        ) : (
                          <ExpandableHTML elements={parseHTML(stringToHTML(firstThreeSentences))} />
                        )}
                      </>
                    )}
                  </div>
                  {!fullDescription && (
                    <>
                      <div className="pt-2 px-2">
                        <RSVPButtons
                          onYes={(e) => anonymousClickWrapper(() => handleRsvpYes(e))}
                          onNo={(e) => anonymousClickWrapper(() => handleRsvpNo(e))}
                          onMaybe={(e) => anonymousClickWrapper(() => handleRsvpMaybe(e))}
                        />
                      </div>
                    </>
                  )}
                  {/* Event Details - full view only */}
                  {fullDescription && (
                    <div className="px-2 pb-4 flex flex-col gap-3 text-sm text-gray-600">
                      {post?.admissionFee !== undefined && (
                        <div className="flex items-center gap-1.5">
                          <img src={fees} className="w-4 h-4" alt="fees" />
                          <span>
                            {post?.admissionFee
                              ? `$${post?.admissionFee}`
                              : "Admission is Free"}
                          </span>
                        </div>
                      )}

                      {post?.eventType && (
                        <div className="flex items-center gap-1.5">
                          <img
                            src={calendar}
                            className="w-4 h-4"
                            alt="calendar"
                          />
                          <span>This event is a {post?.eventType}</span>
                        </div>
                      )}
                      {post?.visibility && (
                        <div className="flex items-center gap-1.5">
                          <img
                            src={publicIcon}
                            className="w-4 h-4"
                            alt="public"
                          />
                          <span>
                            This is a {post?.visibility.toLowerCase()} event
                          </span>
                        </div>
                      )}
                      {(() => {
                        const restricted = post?.ageRestricted;
                        const min = post?.minAge;
                        const max = post?.maxAge;
                        let label;
                        if (min != null && max != null) {
                          label = `Ages ${min} - ${max}`;
                        } else if (min != null) {
                          label = `Not allowed for people below ${min}`;
                        } else if (max != null) {
                          label = `Open for people up to ${max} years old`;
                        } else if (restricted) {
                          label = "Not allowed for people below 14";
                        } else {
                          label = "Open for all ages";
                        }
                        return (
                          <div className="flex items-center gap-1.5">
                            <img src={members} className="w-4 h-4" style={{ filter: "brightness(0) saturate(0%) opacity(55%)" }} alt="age" />
                            <span>{label}</span>
                          </div>
                        );
                      })()}
                      <div className="pt-2">
                        <RSVPButtons
                          onYes={(e) => anonymousClickWrapper(() => handleRsvpYes(e))}
                          onNo={(e) => anonymousClickWrapper(() => handleRsvpNo(e))}
                          onMaybe={(e) => anonymousClickWrapper(() => handleRsvpMaybe(e))}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── NORMAL POST ── */}
              {!isEventPost && (
                <div>
                  <Typography variant="body2" className="font-semibold text-base">
                    {parseHTML(postTitle)}
                  </Typography>

                  {postDescriptions && postDescriptions.length > 0 &&
                    (fullDescription ? (
                      <Typography
                        variant="body2"
                        className="break-words text-base"
                      >
                        {parseHTML(parsedDescriptions[isCarouselPost ? carouselActiveIndex : 0])}
                      </Typography>
                    ) : (
                      <ExpandableHTML elements={parseHTML(stringToHTML(firstThreeSentences))} />
                    ))}
                  <div className="flex gap-1 mt-3 flex-wrap">
                    {post?.tags?.map((tag, key) => (
                      <Typography
                        key={key}
                        onClick={(e) => {
                          e.stopPropagation();
                          anonymousClickWrapper(() => navigate(`/search?search=%23${tag.subcategory}`));
                        }}
                        variant="body2"
                        color="primary"
                        className="font-semibold"
                      >
                        {`{${tag.subcategory}}`}
                        {key < post.tags.length - 1 ? "," : ""}
                      </Typography>
                    ))}
                  </div>

                  {((isCarouselPost ? carouselActiveItem : post)?.media?.currBucketUrl) &&
                    <div {...handlers} className="flex flex-row items-center justify-center w-full group touch-pan-y">
                      {isCarouselPost && !isMobile && post.carousel.items.length > 1 && (
                        <div className="z-1 flex-1 flex justify-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const newIndex = carouselActiveIndex - 1;
                              scrollToPage(newIndex >= 0 ? newIndex : post.carousel.items.length - 1);
                            }}
                            className="bg-transparent text-gray-700 p-2 z-10 flex items-center justify-center cursor-pointer"
                            aria-label="Previous slide"
                          >
                            {!isMobile && <span className="font-bold"><ChevronLeft sx={{ fontSize: "5rem" }} /></span>}
                          </button>
                        </div>
                      )}

                      <div className="w-11/12 overflow-hidden mx-auto">
                        <div
                          className="flex transition-transform duration-300 ease-in-out"
                          style={{ transform: `translateX(-${carouselActiveIndex * 100}%)` }}
                        >
                          {isCarouselPost ? (
                            post.carousel.items.map((item, index) => (
                              <div key={index} className="w-full flex-shrink-0 flex justify-center">
                                {item?.media?.fileType === "video" ? (
                                  <VideoPlayerCard
                                    className="h-52 w-full rounded-2xl overflow-hidden bg-black"
                                    src={item?.media?.currBucketUrl}
                                  />
                                ) : (
                                  <CardMedia
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      anonymousClickWrapper(() => setOpenImageModal(true));
                                    }}
                                    component="img"
                                    className="h-52 w-full rounded-2xl object-contain cursor-pointer"
                                    image={item?.media?.currBucketUrl}
                                    alt="Stream Media"
                                  />
                                )}
                              </div>
                            ))
                          ) : (
                            // non-carousel posts
                            <div className="w-full flex-shrink-0 flex justify-center">
                              {post?.media?.fileType === "video" ? (
                                <VideoPlayerCard
                                  className="h-52 w-full rounded-2xl overflow-hidden bg-black"
                                  src={post?.media?.currBucketUrl}
                                />
                              ) : (
                                <CardMedia
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    anonymousClickWrapper(() => setOpenImageModal(true));
                                  }}
                                  component="img"
                                  className="h-52 w-full rounded-2xl object-contain cursor-pointer"
                                  image={post?.media?.currBucketUrl}
                                  alt="Stream Media"
                                />
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {isCarouselPost && !isMobile && post.carousel.items.length > 1 && (
                        <div className="z-1 flex-1 flex justify-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const newIndex = carouselActiveIndex + 1;
                              scrollToPage(newIndex < post.carousel.items.length ? newIndex : 0);
                            }}
                            className="bg-transparent text-gray-700 p-2 z-10 flex items-center justify-center cursor-pointer"
                            aria-label="Next slide"
                          >
                            {!isMobile && <span className="font-bold"><ChevronRight sx={{ fontSize: "5rem" }} /></span>}
                          </button>
                        </div>
                      )}
                    </div>}
                  {isCarouselPost && post.carousel.items.length > 1 && (
                    <div className="mt-3 flex justify-center gap-2">
                      {Array.from({ length: post.carousel.items.length }).map((_, pageIndex) => (
                        <button
                          key={pageIndex}
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            scrollToPage(pageIndex);
                          }}
                          className={clsx(
                            "h-2 w-2 rounded-full transition-colors border border-gray-600",
                            carouselActiveIndex === pageIndex
                              ? "bg-gray-800"
                              : "bg-white"
                          )}
                          aria-label={`Go to page ${pageIndex + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <CardActions
            onClick={(e) => e.stopPropagation()}
            disableSpacing
            className="mt-3 flex justify-between"
          >
            <div className="flex gap-3 ml-3">
              {actions.map((action) => (
                action.shown && <div key={action.id}>
                  {action.shown &&
                    (action.render ? (
                      action.render({
                        key: action.id,
                        style: { opacity: action.disabled ? 0.2 : 1 },
                        onClick: action.disabled ? null : () => anonymousClickWrapper(action.onClick),
                        alt: "Action",
                      })
                    ) : (
                      <ActionImageComponent action={action} anonymousClickWrapper={anonymousClickWrapper} />
                    ))}
                </div>
              ))}
            </div>
            {votesLoading && <CircularProgress size="1rem" />}
            <div
              style={{ backgroundColor: "#62626214" }}
              className={clsx(
                "flex gap-2 py-1 px-3 rounded-full items-center",
                votesLoading && "hidden",
              )}
            >
              <Typography className="mt-1" color="primary">
                {upvoteCount}
              </Typography>
              {upvoteLoading && <CircularProgress size="1rem" />}
              {!upvoteLoading &&
                (isUpvoted ? (
                  <img
                    alt="up"
                    style={{
                      height: "15px",
                      opacity: readOnly || isCancelled || isEventPast ? 0.3 : 1,
                      pointerEvents: readOnly || isCancelled || isEventPast ? "none" : "auto",
                    }}
                    className="rotate-180"
                    src={VoteDownGreenImg}
                    onClick={() => onRemoveUpvotePost()}
                  />
                ) : (
                  <img
                    alt="up"
                    style={{
                      height: "15px",
                      opacity: readOnly || isCancelled || isEventPast ? 0.3 : 1,
                      pointerEvents: readOnly || isCancelled || isEventPast ? "none" : "auto",
                    }}
                    className="rotate-180"
                    src={VoteDownImg}
                    onClick={() => anonymousClickWrapper(() => onUpvotePost())}
                  />
                ))}
              {downvoteLoading && <CircularProgress size="1rem" />}
              {!downvoteLoading &&
                (isDownvoted ? (
                  <img
                    alt="down"
                    style={{
                      height: "15px",
                      opacity: readOnly || isCancelled || isEventPast ? 0.3 : 1,
                      pointerEvents: readOnly || isCancelled || isEventPast ? "none" : "auto",
                    }}
                    className=""
                    src={VoteDownGreenImg}
                    onClick={() => onRemoveDownVotePost()}
                  />
                ) : (
                  <img
                    alt="down"
                    style={{
                      height: "15px",
                      opacity: readOnly || isCancelled || isEventPast ? 0.3 : 1,
                      pointerEvents: readOnly || isCancelled || isEventPast ? "none" : "auto",
                    }}
                    className=""
                    src={VoteDownImg}
                    onClick={() => anonymousClickWrapper(() => onDownVotePost())}
                  />
                ))}
              <Typography className="mt-1" color="primary">
                {downvoteCount}
              </Typography>
            </div>
          </CardActions>

          {comment && (
            <button onClick={() => anonymousClickWrapper(() => viewPost(post?.id))}>
              <Typography variant="body2" color="text.secondary" className="ml-4">
                View {commentCount} comments
              </Typography>
            </button>
          )}
        </Card>


        {(isCarouselPost ? carouselActiveItem : post)?.media?.fileType !== "video" && (
          <ImageModal
            url={(isCarouselPost ? carouselActiveItem : post)?.media?.currBucketUrl}
            open={openImageModal}
            setOpen={setOpenImageModal}
          />
        )}
        <ShareDialog
          post={post}
          open={postShareDialog}
          onClose={() => setPostShareDialog(false)}
        />
        <div
          style={{ borderColor: theme.palette.primary.main }}
          className="px-3 flex border-t-2 flex-col justify-between"
        ></div>
      </div>
    );
}
